import React, { useState } from 'react';
import { SAMPLE_ARTICLES, WRITERS } from '../constants';
import { motion } from 'motion/react';
import { User, ArrowLeft, Search, Sparkles, BookOpen } from 'lucide-react';
import { useTranslate } from '../context/LanguageContext';
import { cn, getImageUrl } from '../lib/utils';
import { useData } from '../context/DataContext';

export default function Banner() {
  const { language, t } = useTranslate();
  const [query, setQuery] = useState('');
  const { writers, settings } = useData();
  const primaryAuthor = writers?.find(w => w.name === 'نورالدين كوكى' || w.name_en === 'Noureddine Koki' || w.name === 'Noureddine Koki') || WRITERS[0];
  // Use hero_image_url from site settings if available, otherwise fall back to writer avatar
  const heroImageSrc = settings?.hero_image_url || primaryAuthor?.avatar || '';

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      window.location.href = `/articles?q=${encodeURIComponent(query.trim())}`;
    }
  };

  return (
    <section className="relative w-full bg-primary overflow-hidden group">
      {/* Background Dots Grid Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.01)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.01)_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />

      {/* Elegant Radial Glows / Orbs */}
      <div className="absolute right-[5%] top-1/4 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[120px] pointer-events-none animate-pulse duration-[8000ms]" />
      <div className="absolute left-[10%] bottom-1/4 w-[350px] h-[350px] bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gradient-to-r from-secondary/5 via-transparent to-white/5 rounded-full blur-[140px] pointer-events-none" />

      {/* Decorative Elegant Soft Blur Elements */}
      <div className="absolute top-20 left-[10%] w-16 h-16 bg-white/5 rounded-lg rotate-12 backdrop-blur-sm pointer-events-none" />
      <div className="absolute bottom-20 right-[5%] w-24 h-24 bg-white/5 rounded-xl -rotate-12 backdrop-blur-sm pointer-events-none" />
      <div className="absolute top-1/2 left-[30%] w-8 h-8 bg-white/10 rounded rotate-45 pointer-events-none" />
      <div className="absolute top-40 right-[25%] w-12 h-12 bg-white/5 rounded rotate-12 pointer-events-none" />
      <div className="absolute bottom-[30%] left-[45%] w-4 h-4 bg-white/20 rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 lg:px-12 min-h-[500px] lg:h-[650px] flex flex-col lg:flex-row items-center py-12 lg:py-0">
        
        {/* RIGHT side (Visual): Content Block */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={cn(
            "w-full lg:w-1/2 flex flex-col justify-center text-center z-20 mb-12 lg:mb-0",
            language === 'ar' ? "lg:text-right pl-0 lg:pl-12" : "lg:text-left pr-0 lg:pr-12"
          )}
        >
          {/* Elegant Top Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={cn(
              "inline-flex items-center gap-2 bg-secondary/15 border border-secondary/35 text-secondary text-[10px] font-bold uppercase tracking-widest w-fit mb-6 px-4 py-2 rounded-full shadow-inner shadow-black/40 backdrop-blur-md",
              language === 'ar' ? "mx-auto lg:mx-0" : "mx-auto lg:mx-0"
            )}
          >
            <Sparkles size={11} className="text-secondary animate-pulse" />
            <span className="text-white/90">{t('الموقع الرسمي والمدونة الفكرية للكاتب والسياسي')}</span>
          </motion.div>

          {/* Main Elegant Heading */}
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-2 leading-[1.1] tracking-tight"
          >
            {t('نورالدين كوكى')}
          </motion.h1>

          {/* Subtitle Accent */}
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="text-base md:text-lg font-medium text-secondary tracking-wide mb-6"
          >
            {t('رؤى فكرية وقراءات سياسية معاصرة من أجل سودان المستقبل')}
          </motion.h2>

          {/* Re-written description to be highly elegant */}
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className={cn(
              "text-[13px] md:text-[14px] text-white/80 mb-8 md:mb-10 leading-relaxed font-light not-italic max-w-2xl mx-auto lg:mx-0",
              language === 'ar' ? "pr-0 lg:pr-5 border-r-0 lg:border-r-2" : "pl-0 lg:pl-5 border-l-0 lg:border-l-2",
              "border-secondary/30"
            )}
          >
            {t('تفكيك للمشهد السياسي، وتوثيق لمسيرة الوعي الوطني والتحليل المنهجي. نافذة نقدية حرة تبحث في جذور الأزمة وتسعى لصياغة ملامح سودان ديمقراطي ومزدهر.')}
          </motion.p>

          {/* Elegant Action Buttons Container */}
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.42 }}
            className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8 w-full"
          >
            <a 
              href="/articles"
              className="w-full sm:w-auto px-6 py-4 bg-secondary text-white hover:bg-white hover:text-primary transition-all duration-300 shadow-[0_20px_40px_-10px_rgba(255,63,52,0.4)] hover:shadow-none font-bold text-center flex items-center justify-center gap-2 group/btn rounded-full text-xs animate-shimmer"
            >
              <BookOpen size={16} />
              <span>{t('قراءة فصول الآراء والمقالات')}</span>
              <ArrowLeft size={16} className={cn("transform transition-transform duration-300", language === 'ar' ? "group-hover/btn:-translate-x-1.5" : "group-hover/btn:translate-x-1.5 rotate-180")} />
            </a>
            <a 
              href="/about"
              className="w-full sm:w-auto px-6 py-4 bg-white/10 hover:bg-white text-white hover:text-primary border border-white/20 hover:border-white transition-all duration-300 font-bold text-center flex items-center justify-center gap-2 rounded-full text-xs"
            >
              <User size={16} />
              <span>{t('السيرة الذاتية والمسيرة الكفاحية')}</span>
            </a>
          </motion.div>

          {/* Restyled Sleek Form-integrated Search Bar */}
          <motion.form 
            onSubmit={handleSearch}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48 }}
            className="w-full max-w-lg mx-auto lg:mx-0 relative group"
          >
            <div className="relative overflow-hidden rounded-full border border-white/10 group-focus-within:border-secondary/40 transition-all bg-white/5 backdrop-blur-md">
              <input 
                type="text" 
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('ابحث في فصول ومواضيع المدونة...')}
                className={cn(
                  "w-full bg-transparent text-white placeholder:text-white/40 py-3.5 focus:outline-none transition-all text-xs font-medium",
                  language === 'ar' ? "text-right pr-12 pl-12" : "text-left pl-12 pr-12"
                )}
              />
              <Search className={cn("absolute top-1/2 -translate-y-1/2 text-white/50 group-focus-within:text-white transition-colors animate-pulse", language === 'ar' ? "right-4" : "left-4")} size={16} />
              <button 
                type="submit"
                className={cn("absolute top-1/2 -translate-y-1/2 text-[10px] font-bold bg-white/10 hover:bg-secondary text-white px-3 py-1.5 rounded-full transition-all", language === 'ar' ? "left-3" : "right-3")}
              >
                {t('بحث المدونة')}
              </button>
            </div>
          </motion.form>
        </motion.div>

        {/* LEFT side (Visual): Hero Image */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, rotate: 5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="lg:w-1/2 h-full flex items-center justify-center relative pt-10 lg:pt-0"
        >
           <div className="relative group/img">
              {/* Backglow Ambient Glow */}
              <div className="absolute -inset-6 bg-gradient-to-tr from-secondary/30 via-transparent to-secondary/10 rounded-[2.5rem] blur-3xl opacity-60 group-hover/img:opacity-100 transition-opacity duration-1000 pointer-events-none" />

              {/* Decorative Layered Border Frames */}
              <div className="absolute -inset-4 border border-white/10 rounded-3xl rotate-3 transition-transform duration-1000 group-hover/img:rotate-[1deg] group-hover/img:scale-[1.01]" />
              <div className="absolute -inset-2 border border-secondary/25 rounded-3xl -rotate-2 transition-transform duration-1000 group-hover/img:rotate-[-1deg] group-hover/img:scale-[1.02]" />
              
              {/* Gold Accent Corner Frames */}
              <div className="absolute top-[-10px] right-[-10px] w-10 h-10 border-t-2 border-r-2 border-secondary rounded-tr-xl z-20 pointer-events-none transition-all duration-700 group-hover/img:translate-x-[-4px] group-hover/img:translate-y-[4px]" />
              <div className="absolute bottom-[-10px] left-[-10px] w-10 h-10 border-b-2 border-l-2 border-secondary rounded-bl-xl z-20 pointer-events-none transition-all duration-700 group-hover/img:translate-x-[4px] group-hover/img:translate-y-[-4px]" />

              <div className="relative w-[280px] lg:w-[420px] h-[280px] lg:h-[420px] overflow-hidden rounded-3xl border-4 border-white/25 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.8)] z-10">
                <img 
                  src={getImageUrl(heroImageSrc)} 
                  alt={primaryAuthor?.name || 'نورالدين كوكى'} 
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-cover transition-all duration-1000 scale-100 group-hover/img:scale-105 group-hover/img:brightness-105"
                />
                
                {/* Micro reflection shimmer */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover/img:animate-[shimmer_1.5s_infinite] pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/50 via-transparent to-transparent opacity-70 pointer-events-none" />
              </div>
           </div>
           
           {/* Floating Accent Orbs */}
           <div className="absolute top-1/4 -right-12 w-4 h-4 bg-secondary rounded-full animate-pulse shadow-[0_0_20px_rgba(220,180,58,0.6)]" />
           <div className="absolute bottom-1/4 -left-6 w-2.5 h-2.5 bg-white/30 rounded-full" />
        </motion.div>

      </div>
    </section>
  );
}
